import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { ContractData, createContract, previewContract } from "../../services/contracts";

type Field = { key: keyof ContractData; label: string; required?: boolean; half?: boolean };

const ENTITY: Field[] = [
  { key: "name",    label: "Legal name", required: true },
  { key: "tax_id",  label: "CUIT", required: true, half: true },
  { key: "dba",     label: "Trade name (DBA)", required: true, half: true },
];
const PERSON: Field[] = [
  { key: "name",    label: "First name", required: true, half: true },
  { key: "surname", label: "Last name", half: true },
  { key: "tax_id",  label: "CUIT / CUIL", required: true, half: true },
  { key: "dba",     label: "Trade name (DBA)", required: true, half: true },
];
const ADDRESS: Field[] = [
  { key: "addr_street",      label: "Street", required: true, half: true },
  { key: "addr_number",      label: "Number", required: true, half: true },
  { key: "addr_floor",       label: "Floor", half: true },
  { key: "addr_office",      label: "Office", half: true },
  { key: "addr_postal_code", label: "Postal code", required: true, half: true },
  { key: "addr_city",        label: "City", required: true, half: true },
  { key: "addr_state",       label: "Province", required: true, half: true },
];
const REP: Field[] = [
  { key: "rep_name",    label: "First name", required: true, half: true },
  { key: "rep_surname", label: "Last name", required: true, half: true },
  { key: "rep_id",      label: "DNI", required: true, half: true },
  { key: "rep_title",   label: "Title (e.g. President, Managing partner)", required: true, half: true },
];

/**
 * Generar el contrato de un colector que no lo tiene. Dos pasos: completar los
 * datos (precargados de la config) → vista previa del contrato armado → guardar.
 * Usa la misma plantilla y el mismo formato que el onboarding del portal vendor.
 */
export default function ContractDialog({ open, collectorId, prefill, onClose, onCreated }: {
  open: boolean;
  collectorId: string;
  prefill: ContractData;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [data, setData] = useState<ContractData>(prefill);
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  useEffect(() => { if (open) { setData(prefill); setHtml(null); setErr(null); } }, [open, prefill]);

  const pj = data.tax_category === "pj";
  const groups: [string, Field[]][] = [
    [pj ? "Company" : "Person", pj ? ENTITY : PERSON],
    ["Legal address", ADDRESS],
    ...(pj ? [["Legal representative", REP] as [string, Field[]]] : []),
  ];
  const missing = data.tax_category === ""
    || groups.some(([, fs]) => fs.some((f) => f.required && !String(data[f.key] ?? "").trim()));

  const set = (k: keyof ContractData, v: string) => { setData((d) => ({ ...d, [k]: v })); setHtml(null); };

  async function onPreview() {
    setBusy(true); setErr(null);
    try { setHtml(await previewContract(collectorId, data)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setErr(null);
    try { await createContract(collectorId, data); onCreated(); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Generate contract — {collectorId}
        <Typography variant="body2" color="text.secondary">
          {html ? "Review the contract before saving. Once saved it can't be overwritten from here."
                : "Same template the vendor-portal onboarding uses. Data is prefilled from Config; complete what's missing."}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {html ? (
          <Box component="iframe" title="Contract preview" srcDoc={html} sandbox=""
            sx={{ width: "100%", height: "60vh", border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "#fff" }} />
        ) : (
          <Stack spacing={2.5}>
            <TextField select size="small" label="Taxpayer type" value={data.tax_category} required
              onChange={(e) => set("tax_category", e.target.value)} sx={{ maxWidth: 320 }}>
              <MenuItem value="pj">Legal entity (persona jurídica)</MenuItem>
              <MenuItem value="ph">Individual (persona humana)</MenuItem>
            </TextField>
            {data.tax_category !== "" && groups.map(([title, fields]) => (
              <Box key={title}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{title}</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                  {fields.map((f) => (
                    <TextField key={f.key} size="small" label={f.label} required={f.required}
                      value={data[f.key]} onChange={(e) => set(f.key, e.target.value)}
                      inputProps={{ maxLength: 200 }}
                      sx={{ gridColumn: f.half ? undefined : "1 / -1" }} />
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {html ? (
          <>
            <Button onClick={() => setHtml(null)} disabled={busy}>Back to edit</Button>
            <Button variant="contained" onClick={onSave} disabled={busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
              {busy ? "Saving…" : "Save contract"}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onPreview} disabled={busy || missing}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {busy ? "Building…" : "Preview"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
